import { useState, useEffect, useRef } from 'react';

interface IntroAnimationProps {
  onComplete: () => void;
}

export default function IntroAnimation({ onComplete }: IntroAnimationProps) {
  const [phase, setPhase] = useState<'particles' | 'logo' | 'text' | 'flash' | 'done'>('particles');
  const [logoVisible, setLogoVisible] = useState(false);
  const [textVisible, setTextVisible] = useState(false);
  const [subTextVisible, setSubTextVisible] = useState(false);
  const [flashActive, setFlashActive] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const waveCanvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const waveFrameRef = useRef<number>(0);

  // Particle canvas animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles: {
      x: number; y: number; vx: number; vy: number;
      size: number; alpha: number; color: string; life: number; maxLife: number;
    }[] = [];

    const colors = ['#22d3ee', '#a855f7', '#ec4899', '#ffffff', '#7c3aed'];

    for (let i = 0; i < 180; i++) {
      const maxLife = 80 + Math.random() * 120;
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 1.2,
        vy: (Math.random() - 0.5) * 1.2,
        size: Math.random() * 2.5 + 0.5,
        alpha: Math.random() * 0.8 + 0.2,
        color: colors[Math.floor(Math.random() * colors.length)],
        life: Math.random() * maxLife,
        maxLife,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 100) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(168,85,247,${0.15 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      particles.forEach((p) => {
        p.life++;
        if (p.life > p.maxLife) {
          p.life = 0;
          p.x = Math.random() * canvas.width;
          p.y = Math.random() * canvas.height;
        }
        const lifeRatio = p.life / p.maxLife;
        const alpha = lifeRatio < 0.2
          ? (lifeRatio / 0.2) * p.alpha
          : lifeRatio > 0.8
          ? ((1 - lifeRatio) / 0.2) * p.alpha
          : p.alpha;

        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.shadowBlur = 8;
        ctx.shadowColor = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.globalAlpha = alpha;
        ctx.fillStyle = p.color;
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.shadowBlur = 0;
      });

      animFrameRef.current = requestAnimationFrame(draw);
    };

    draw();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Wave / water flow canvas animation
  useEffect(() => {
    const canvas = waveCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = 600;
    canvas.height = 120;

    let tick = 0;

    const drawWave = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      tick += 0.04;

      // Draw multiple wave layers
      const waves = [
        { amp: 8, freq: 0.018, speed: 1.0, color: 'rgba(34,211,238,0.35)', yBase: 60 },
        { amp: 6, freq: 0.025, speed: 1.5, color: 'rgba(168,85,247,0.30)', yBase: 62 },
        { amp: 10, freq: 0.012, speed: 0.7, color: 'rgba(236,72,153,0.20)', yBase: 58 },
        { amp: 4, freq: 0.035, speed: 2.0, color: 'rgba(255,255,255,0.15)', yBase: 60 },
      ];

      waves.forEach((wave) => {
        ctx.beginPath();
        ctx.moveTo(0, canvas.height);
        for (let x = 0; x <= canvas.width; x += 2) {
          const y = wave.yBase + Math.sin(x * wave.freq + tick * wave.speed) * wave.amp
            + Math.sin(x * wave.freq * 1.7 + tick * wave.speed * 0.8) * (wave.amp * 0.4);
          ctx.lineTo(x, y);
        }
        ctx.lineTo(canvas.width, canvas.height);
        ctx.closePath();
        ctx.fillStyle = wave.color;
        ctx.fill();
      });

      // Flowing highlight line on top
      ctx.beginPath();
      for (let x = 0; x <= canvas.width; x += 2) {
        const y = 55 + Math.sin(x * 0.02 + tick * 1.2) * 9
          + Math.sin(x * 0.035 + tick * 0.9) * 4;
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      const lineGrad = ctx.createLinearGradient(0, 0, canvas.width, 0);
      lineGrad.addColorStop(0, 'rgba(34,211,238,0)');
      lineGrad.addColorStop(0.3, 'rgba(34,211,238,0.8)');
      lineGrad.addColorStop(0.6, 'rgba(168,85,247,0.8)');
      lineGrad.addColorStop(1, 'rgba(236,72,153,0)');
      ctx.strokeStyle = lineGrad;
      ctx.lineWidth = 1.5;
      ctx.shadowBlur = 10;
      ctx.shadowColor = 'rgba(34,211,238,0.8)';
      ctx.stroke();
      ctx.shadowBlur = 0;

      waveFrameRef.current = requestAnimationFrame(drawWave);
    };

    drawWave();

    return () => {
      cancelAnimationFrame(waveFrameRef.current);
    };
  }, []);

  // Animation sequence timing
  useEffect(() => {
    const t1 = setTimeout(() => setLogoVisible(true), 600);
    const t2 = setTimeout(() => setTextVisible(true), 1400);
    const t3 = setTimeout(() => setSubTextVisible(true), 2200);
    const t4 = setTimeout(() => setFlashActive(true), 3400);
    const t5 = setTimeout(() => setFadeOut(true), 3700);
    const t6 = setTimeout(() => onComplete(), 4400);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
      clearTimeout(t6);
    };
  }, [onComplete]);

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center transition-opacity duration-700 ${
        fadeOut ? 'opacity-0 pointer-events-none' : 'opacity-100'
      }`}
      style={{ background: 'radial-gradient(ellipse at center, #0d0020 0%, #000000 70%)' }}
    >
      {/* Particle canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

      {/* Radial glow center */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(168,85,247,0.18) 0%, transparent 70%)',
        }}
      />

      {/* Flash overlay */}
      <div
        className={`absolute inset-0 bg-white pointer-events-none transition-opacity duration-300 ${
          flashActive ? 'opacity-60' : 'opacity-0'
        }`}
      />

      {/* Center content */}
      <div className="relative z-10 flex flex-col items-center gap-4 select-none">

        {/* Logo - 圖片 Logo 發光效果 */}
        <div
          className={`transition-all duration-700 ${
            logoVisible
              ? 'opacity-100 scale-100 translate-y-0'
              : 'opacity-0 scale-75 translate-y-8'
          }`}
        >
          <div className="relative flex items-center justify-center">
            {/* 外層光暈 */}
            <div
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{
                background: 'radial-gradient(ellipse 120% 120% at 50% 50%, rgba(34,211,238,0.25) 0%, rgba(168,85,247,0.15) 50%, transparent 75%)',
                filter: 'blur(16px)',
                transform: 'scale(2)',
              }}
            />
            {/* 圖片 Logo */}
            <div className="relative w-28 h-28 flex items-center justify-center">
              <img
                src="https://storage.readdy-site.link/project_files/2d96c7ea-03ca-4f90-a66b-a881a6a23467/11e329a1-415a-44ab-8899-0ac1238aea4c_Gemini_Generated_Image_yx64hoyx64hoyx64-1.png?v=8e4f1ac532bb393498e97882a0e5b5fc"
                alt="四相AI影視學院 Logo"
                className="w-full h-full object-contain"
                style={{
                  filter: 'drop-shadow(0 0 16px rgba(34,211,238,0.9)) drop-shadow(0 0 36px rgba(168,85,247,0.6)) brightness(1.1)',
                  animation: logoVisible ? 'logoPulse 3s ease-in-out infinite' : 'none',
                }}
              />
            </div>
          </div>
        </div>

        {/* Main title with water flow effect */}
        <div
          className={`text-center transition-all duration-700 ${
            textVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-6'
          }`}
        >
          {/* Title text with liquid shimmer */}
          <div className="relative inline-block">
            <h1
              className="text-5xl font-bold tracking-widest relative z-10"
              style={{
                fontFamily: '\'Noto Serif TC\', serif',
                letterSpacing: '0.15em',
                background: 'linear-gradient(135deg, #ffffff 0%, #22d3ee 25%, #ffffff 50%, #a855f7 75%, #22d3ee 100%)',
                backgroundSize: '300% 300%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: textVisible ? 'liquidFlow 3s ease-in-out infinite' : 'none',
                filter: 'drop-shadow(0 0 20px rgba(34,211,238,0.6)) drop-shadow(0 0 40px rgba(168,85,247,0.4))',
              }}
            >
              四相AI影視學院
            </h1>

            {/* Shimmer sweep overlay */}
            <div
              className="absolute inset-0 pointer-events-none z-20"
              style={{
                background: 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.25) 50%, transparent 70%)',
                backgroundSize: '200% 100%',
                animation: textVisible ? 'shimmerSweep 2.5s ease-in-out infinite' : 'none',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
              }}
            />
          </div>

          {/* Wave canvas under title */}
          <div
            className={`mt-1 transition-all duration-1000 ${
              textVisible ? 'opacity-100' : 'opacity-0'
            }`}
            style={{ width: 480, margin: '4px auto 0' }}
          >
            <canvas
              ref={waveCanvasRef}
              style={{ width: '100%', height: 50, display: 'block' }}
            />
          </div>

          {/* Subtitle */}
          <div
            className="mt-1 text-sm tracking-[0.5em] font-light"
            style={{
              fontFamily: '\'Noto Serif TC\', serif',
              background: 'linear-gradient(90deg, #22d3ee, #a855f7, #ec4899, #22d3ee)',
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: textVisible ? 'liquidFlow 4s ease-in-out infinite reverse' : 'none',
            }}
          >
            專業AI影視教學與數位特效製作
          </div>
        </div>

        {/* Sub text */}
        <div
          className={`transition-all duration-700 delay-100 ${
            subTextVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-4'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="h-px w-20 bg-gradient-to-r from-transparent to-cyan-400" />
            <span
              className="text-xs tracking-[0.5em] text-cyan-300/80 font-light"
              style={{ fontFamily: '\'Noto Serif TC\', serif' }}
            >
              SIXIANG · AI · ACADEMY
            </span>
            <div className="h-px w-20 bg-gradient-to-l from-transparent to-cyan-400" />
          </div>
        </div>

        {/* Animated ring */}
        <div
          className={`absolute transition-all duration-1000 ${
            logoVisible ? 'opacity-30 scale-100' : 'opacity-0 scale-50'
          }`}
          style={{ width: 360, height: 360, top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
        >
          <div
            className="w-full h-full rounded-full border border-purple-500/40"
            style={{ animation: 'spin 8s linear infinite' }}
          />
          <div
            className="absolute inset-4 rounded-full border border-cyan-400/30"
            style={{ animation: 'spin 5s linear infinite reverse' }}
          />
          <div
            className="absolute inset-10 rounded-full border border-pink-500/20"
            style={{ animation: 'spin 12s linear infinite' }}
          />
        </div>

        {/* Loading bar */}
        <div
          className={`w-56 h-px bg-white/10 rounded-full overflow-hidden mt-2 transition-opacity duration-500 ${
            subTextVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div
            className="h-full rounded-full"
            style={{
              background: 'linear-gradient(90deg, #22d3ee, #a855f7, #ec4899)',
              animation: 'loadBar 1.8s ease-out forwards',
              boxShadow: '0 0 8px rgba(168,85,247,0.8)',
            }}
          />
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes loadBar {
          from { width: 0%; }
          to { width: 100%; }
        }
        @keyframes liquidFlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes shimmerSweep {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes waterDrop {
          0% { transform: translateY(-10px) scaleX(0.8); opacity: 0; }
          50% { opacity: 1; }
          100% { transform: translateY(10px) scaleX(1.1); opacity: 0; }
        }
        @keyframes logoPulse {
          0%, 100% { filter: drop-shadow(0 0 16px rgba(34,211,238,0.9)) drop-shadow(0 0 36px rgba(168,85,247,0.6)) brightness(1.1); }
          50% { filter: drop-shadow(0 0 28px rgba(34,211,238,1)) drop-shadow(0 0 60px rgba(168,85,247,0.9)) brightness(1.25); }
        }
      `}</style>
    </div>
  );
}
